/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { Separator } from '@ba/ba-ui-carbon-toolkit';
import configure16 from '@ba/ba-graphics/dist/icons-js/configure_16';
import refined16 from '@ba/ba-graphics/dist/icons-js/refined_16';
import textLinkAnalysis32 from '@ba/ba-graphics/dist/icons-js/text-link--analysis_32';

import FilterDock from 'components/filterDock/FilterDock';
import StringResources from 'nls/StringResources';

import ToolbarSection from 'components/toolbar/ToolbarSection';
import { ToolbarItem } from 'components/toolbar/items';

@observer
class CanvasToolbar extends Component {

	constructor(props) {
		super(props);
		this.state = {
			selectedTab: undefined
		};
	}

	render() {

		const {
			exploreVM,
			dashboardApi
		} = this.props;

		const actionSeparator = <Separator className="explore-vis-toolbar__separator" orientation='vertical' />;

		const compareModel = this._getCompareModelOnSelectedCard();
		const filterDockModel = this._getFilterDockModelOnSelectedCard();
		const showFilterDock = filterDockModel && this._showFilterDock();

		let filterDock = null;
		if (showFilterDock) {
			filterDock = <FilterDock
				filterDockModel={filterDockModel}
				dashboardApi={dashboardApi}
			/>;
		}

		if (!compareModel) {
			this._closeFilterDock();
		}

		const selectedTab = this.props.exploreVM.canvasTabs.tab;
		const isStartingPointsDisplayed = exploreVM.isStartingPointsDisplayed();

		return (
			<ToolbarSection align='right'>
				{actionSeparator}
				{!isStartingPointsDisplayed && <ToolbarItem
					kind={'ghost'}
					iconId={textLinkAnalysis32.id}
					selected={selectedTab === 'details'}
					label={StringResources.get('exploreToolbar_details')}
					title={StringResources.get('exploreToolbar_details')}
					onPrimaryAction={this._showDetailsClick}
					id="details"
				/>}
				{!isStartingPointsDisplayed && actionSeparator}
				{filterDock}
				{!!filterDock && actionSeparator}
				{!isStartingPointsDisplayed && <ToolbarItem
					kind={'ghost'}
					iconId={refined16.id}
					selected={selectedTab === 'slots'}
					label={StringResources.get('slot_editor')}
					title={StringResources.get('slot_editor')}
					onPrimaryAction={this._showFieldsClick}
					id="fields"
				/>}
				{!isStartingPointsDisplayed && actionSeparator}
				{!isStartingPointsDisplayed && <ToolbarItem
					kind={'ghost'}
					iconId={configure16.id}
					selected={selectedTab === 'properties'}
					label={StringResources.get('propertiesPanelLabel')}
					title={StringResources.get('propertiesPanelLabel')}
					onPrimaryAction={this._showPropertiesClick}
					id="properties"
				/>}
			</ToolbarSection>
		);
	}

	_closeFilterDock() {
		this.props.dashboardApi.triggerDashboardEvent('filterDock:collapse');
	}

	_showDetailsClick = () => {
		if (this.props.exploreVM.canvasTabs.tab === 'details' && this.props.exploreVM.showCanvasSidePanel) {
			this.props.exploreVM.toggleCanvasSidePanel();
			return;
		}
		if (!this.props.exploreVM.showCanvasSidePanel) {
			this.props.exploreVM.toggleCanvasSidePanel();
		}
		this.props.exploreVM.canvasTabs.showTab('details');
		this._setSelectedTabState('details');
	}
	_showFieldsClick = () => {
		if (this.props.exploreVM.canvasTabs.tab === 'slots' && this.props.exploreVM.showCanvasSidePanel) {
			this.props.exploreVM.toggleCanvasSidePanel();
			return;
		}
		if (!this.props.exploreVM.showCanvasSidePanel) {
			this.props.exploreVM.toggleCanvasSidePanel();
		}
		this.props.exploreVM.canvasTabs.showTab('slots');
		this._setSelectedTabState('slots');
	}
	_showPropertiesClick = () => {
		if (this.props.exploreVM.canvasTabs.tab === 'properties' && this.props.exploreVM.showCanvasSidePanel) {
			this.props.exploreVM.toggleCanvasSidePanel();
			return;
		}
		if (!this.props.exploreVM.showCanvasSidePanel) {
			this.props.exploreVM.toggleCanvasSidePanel();
		}
		this.props.exploreVM.canvasTabs.showTab('properties');
		this._setSelectedTabState('properties');
	}

	_setSelectedTabState(id) {
		this.setState({
			selectedTab: id
		});
	}

	_getCompareModelOnSelectedCard() {
		const selectedCardId = this._getSelectedCard();
		return (this._getCompareCard(selectedCardId) || {}).compareModel;
	}

	_getFilterDockModelOnSelectedCard() {
		const selectedCardId = this._getSelectedCard();
		return (this._getCompareCard(selectedCardId) || this._getStartingPointsModel() || {}).filterDockModel;
	}

	_showFilterDock() {
		const selectedCardId = this._getSelectedCard();
		const { showFilterDock } = (this._getCompareCard(selectedCardId) || this._getStartingPointsModel() || {});
		return !showFilterDock || showFilterDock();
	}

	_getCompareCard(id) {
		if (id) {
			const item = this._getCard(id);
			if (item && item.subType === 'compare') {
				return item;
			}
		}
	}

	_getStartingPointsModel() {
		if (this.props.exploreVM.isStartingPointsDisplayed() && this.props.exploreVM.isFilterStartingPointsEnabled()) {
			return this.props.boardModel.layout.startingPointsModel;
		}
	}

	_getCard(id) {
		return id && this.props.boardModel.layout.findModel(id);
	}

	_getSelectedCard() {
		const exploreVM = this.props.exploreVM;
		return !exploreVM.isStartingPointsDisplayed() ? exploreVM.getDisplayedCard() : undefined;
	}
}

CanvasToolbar.propTypes = {
	exploreVM: PropTypes.object.isRequired,
	boardModel: PropTypes.object.isRequired,
	dashboardApi: PropTypes.object.isRequired
};

export default CanvasToolbar;



// WEBPACK FOOTER //
// ./src/js/layout/explorecontainer/CanvasToolbar.js